import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { useAuth, useTheme, api, API } from "../App";
import {
  Copy,
  Check,
  SkipForward,
  RefreshCw,
  Sparkles,
  Image,
  ChevronLeft,
  ChevronRight,
  Pencil,
  X,
  Download,
  Settings,
  Star,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import AppShell from "../components/AppShell";
import UpgradePrompt from "../components/UpgradePrompt";
import { useSubscription } from "../hooks/useSubscription";
import { cleanPostContent } from "../utils/postContent";

const TOPIC_LABELS = {
  "ai-ml": "AI & ML",
  "cloud": "Cloud",
  "devops": "DevOps",
  "security": "Security",
  "web-dev": "Web Dev",
  "mobile": "Mobile",
  "data-science": "Data Science",
  "blockchain": "Blockchain",
  "engineering": "Engineering",
  "product": "Product",
  "ux-design": "UX Design",
  "startup": "Startup",
  "leadership": "Leadership",
  "career": "Career",
  "open-source": "Open Source",
  "programming": "Programming",
};

const IMAGE_TYPE_LABELS = {
  infographic: "Infographic",
  carousel: "Carousel",
  dalle: "AI Image",
};

const PostCard = ({ post, user, onCopy, onMarkUsed, onSkip, onEdit, onStar, onUpgradeRequired, copiedId, starredIds }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [activeHookIdx, setActiveHookIdx] = useState(-1); // -1 = primary

  const carouselData = (() => {
    if (!post.carousel_data) return null;
    if (typeof post.carousel_data === "object") return post.carousel_data;
    try { return JSON.parse(post.carousel_data); } catch { return null; }
  })();

  const hookAlts = post.hook_alternatives || [];
  const activeVariantContent = activeHookIdx >= 0 && hookAlts[activeHookIdx]
    ? (() => {
        // Swap the hook (first paragraph) from the alternative into the primary body
        const altHook = cleanPostContent(hookAlts[activeHookIdx].variant_content).split("\n\n")[0];
        const primaryBody = cleanPostContent(post.variant_content).split("\n\n").slice(1).join("\n\n");
        return primaryBody ? `${altHook}\n\n${primaryBody}` : altHook;
      })()
    : cleanPostContent(post.variant_content);
  const content = activeVariantContent;
  const shouldTruncate = content.length > 400;
  const displayContent = shouldTruncate && !isExpanded
    ? content.substring(0, 400)
    : content;

  const handleEditStart = () => {
    setEditContent(post.variant_content);
    setIsEditing(true);
    setIsExpanded(true);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setEditContent("");
  };

  const handleEditSave = async () => {
    if (!editContent.trim()) return;
    setIsSaving(true);
    try {
      await api.patch(`/posts/${post.id}`, { variant_content: editContent });
      onEdit(post.id, editContent);
      setIsEditing(false);
      toast.success("Post updated");
    } catch {
      toast.error("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  const backendUrl = API.replace(/\/api$/, "");
  const [imageUrl, setImageUrl] = useState(
    post.image_path ? `${backendUrl}/static/images/${post.image_path}` : null
  );
  const [carouselUrls, setCarouselUrls] = useState(
    post.carousel_paths ? post.carousel_paths.map(p => `${backendUrl}/static/images/${p}`) : []
  );
  const [imageStatus, setImageStatus] = useState(post.image_status || null);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [carouselIdx, setCarouselIdx] = useState(0);
  const [showGenerateConfirm, setShowGenerateConfirm] = useState(false);

  const handleGenerateImage = async () => {
    setIsGeneratingImage(true);
    try {
      const res = await api.post(`/posts/${post.id}/generate-image`);
      if (res.data.image_url) setImageUrl(res.data.image_url);
      if (res.data.carousel_urls) setCarouselUrls(res.data.carousel_urls);
      setImageStatus("done");
    } catch (error) {
      const code = error?.response?.data?.detail?.code;
      if (code === "upgrade_required" || code === "image_daily_limit_reached") {
        onUpgradeRequired?.(code);
      } else {
        toast.error("Image generation failed");
        setImageStatus("failed");
      }
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      if (post.image_type === "carousel") {
        const { jsPDF } = await import("jspdf");
        const PAGE_W = 216, PAGE_H = 270;
        const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: [PAGE_W, PAGE_H] });
        for (let i = 0; i < carouselUrls.length; i++) {
          if (i > 0) doc.addPage([PAGE_W, PAGE_H], "portrait");
          const resp = await fetch(carouselUrls[i]);
          if (!resp.ok) throw new Error(`Slide ${i + 1} failed`);
          const blob = await resp.blob();
          const dataUrl = await new Promise((res, rej) => {
            const r = new FileReader();
            r.onload = () => res(r.result);
            r.onerror = rej;
            r.readAsDataURL(blob);
          });
          doc.addImage(dataUrl, "PNG", 0, 0, PAGE_W, PAGE_H);
        }
        doc.save(`carousel_${post.id}.pdf`);
      } else {
        const resp = await fetch(imageUrl);
        if (!resp.ok) throw new Error("Download failed");
        const blob = await resp.blob();
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objUrl;
        a.download = imageUrl.split("/").pop() || `post_${post.id}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(objUrl);
      }
      toast.success("Download started");
    } catch {
      toast.error("Download failed. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const isStarred = starredIds?.has(post.id);

  return (
    <div
      className="bg-card rounded-xl border border-border hover:border-primary/25 transition-all duration-200 overflow-hidden min-w-0"
      data-testid={`post-card-${post.id}`}
    >
      {/* ── Header: avatar + meta + star ── */}
      <div className="flex items-start gap-3 px-4 pt-4 pb-2">
        {user?.picture ? (
          <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full shrink-0" />
        ) : (
          <div className="w-9 h-9 bg-gradient-to-br from-primary to-violet-600 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0">
            {user?.name?.charAt(0) || "U"}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-headline font-bold text-sm text-foreground leading-tight">
            {user?.name || "You"}
          </p>
          <p className="text-xs text-muted-foreground leading-snug mt-0.5 truncate">
            {TOPIC_LABELS[post.topic] || post.topic}
          </p>
          {/* Badges — named scores for scannability */}
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            {post.is_trending && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-orange-500/10 text-orange-500 text-[10px] font-semibold">
                🔥 Trending
              </span>
            )}
            {post.story_potential_score != null && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 text-[10px] font-semibold">
                Story {post.story_potential_score}/10
              </span>
            )}
            {post.relatability_score != null && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-500 text-[10px] font-semibold">
                Relatable {post.relatability_score}/10
              </span>
            )}
            {post.angle && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-[10px] font-medium">
                {post.angle}
              </span>
            )}
          </div>
        </div>
        {/* Star lives in header — it's a save action, not a primary action */}
        <button
          onClick={() => onStar(post)}
          data-testid={`star-btn-${post.id}`}
          title={isStarred ? "Remove from saved" : "Save post"}
          className={`p-1.5 rounded-lg transition-colors shrink-0 ${
            isStarred
              ? "text-amber-500 hover:bg-amber-500/10"
              : "text-muted-foreground hover:text-amber-500 hover:bg-muted"
          }`}
        >
          <Star className={`w-4 h-4 ${isStarred ? "fill-amber-500" : ""}`} />
        </button>
      </div>

      {/* ── Hook alternatives ── */}
      {hookAlts.length > 0 && !isEditing && (
        <div className="px-4 pb-2 flex flex-wrap gap-1.5">
          <button
            onClick={() => setActiveHookIdx(-1)}
            className={`px-2 py-0.5 rounded-full text-xs border transition-all ${
              activeHookIdx === -1
                ? "border-primary bg-primary text-primary-foreground font-medium"
                : "border-border text-muted-foreground hover:border-primary/50"
            }`}
          >
            Original
          </button>
          {hookAlts.map((alt, i) => (
            <button
              key={alt.id}
              onClick={() => setActiveHookIdx(activeHookIdx === i ? -1 : i)}
              className={`px-2 py-0.5 rounded-full text-xs border transition-all ${
                activeHookIdx === i
                  ? "border-primary bg-primary text-primary-foreground font-medium"
                  : "border-border text-muted-foreground hover:border-primary/50"
              }`}
            >
              {alt.variant_type?.replace("Hook-", "") || `Hook ${i + 1}`}
            </button>
          ))}
        </div>
      )}

      {/* ── Content ── */}
      <div className="px-4 pb-3">
        {isEditing ? (
          <div className="space-y-2">
            <textarea
              className="w-full min-h-[200px] rounded-lg border border-border bg-background text-foreground text-sm leading-relaxed p-3 resize-y focus:outline-none focus:ring-2 focus:ring-primary"
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              autoFocus
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={handleEditCancel}
                disabled={isSaving}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <X className="w-3.5 h-3.5" />
                Cancel
              </button>
              <button
                onClick={handleEditSave}
                disabled={isSaving || !editContent.trim()}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
              >
                {isSaving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Save
              </button>
            </div>
          </div>
        ) : post.content_format === "carousel" && carouselData ? (
          <div className="space-y-2">
            <div className="flex items-center gap-1.5 mb-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                Carousel · {carouselData.slides?.length || 0} slides
              </span>
            </div>
            <p className="font-semibold text-foreground text-sm">{carouselData.hook_title}</p>
            {carouselData.hook_subtitle && (
              <p className="text-xs text-muted-foreground italic">{carouselData.hook_subtitle}</p>
            )}
            <div className="space-y-1.5 mt-2">
              {(isExpanded ? carouselData.slides : carouselData.slides?.slice(0, 3))?.map((slide, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <span className="text-xs font-bold text-primary bg-primary/10 rounded-full w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                    {slide.slide_number || i + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground leading-tight">{slide.title}</p>
                    <p className="text-xs text-muted-foreground leading-snug">{slide.body}</p>
                  </div>
                </div>
              ))}
              {!isExpanded && carouselData.slides?.length > 3 && (
                <button
                  onClick={() => setIsExpanded(true)}
                  className="text-muted-foreground text-xs hover:text-primary hover:underline ml-7"
                >
                  +{carouselData.slides.length - 3} more slides…
                </button>
              )}
              {isExpanded && carouselData.slides?.length > 3 && (
                <button
                  onClick={() => setIsExpanded(false)}
                  className="text-muted-foreground text-xs hover:text-primary hover:underline ml-7"
                >
                  Show less
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            <p className="text-foreground whitespace-pre-wrap leading-relaxed text-sm break-words">
              {displayContent}
              {shouldTruncate && !isExpanded && (
                <button
                  onClick={() => setIsExpanded(true)}
                  className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline ml-0.5 text-sm font-semibold"
                  data-testid={`expand-btn-${post.id}`}
                >
                  {" "}see more
                </button>
              )}
            </p>
            {shouldTruncate && isExpanded && (
              <button
                onClick={() => setIsExpanded(false)}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline text-xs mt-1 font-semibold"
                data-testid={`expand-btn-${post.id}`}
              >
                Show less
              </button>
            )}
          </>
        )}
      </div>

      {/* ── Takeaway — visually distinct callout box ── */}
      {post.takeaway && (
        <div className="mx-4 mb-3 px-3 py-2.5 bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg flex gap-2.5 items-start">
          <Sparkles className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-[10px] text-slate-500 dark:text-slate-400 font-semibold uppercase tracking-widest mb-1">Takeaway</p>
            <p className="text-xs text-slate-800 dark:text-slate-200 leading-relaxed">{post.takeaway}</p>
          </div>
        </div>
      )}

      {/* ── Image Section ── */}
      {post.image_type && (
        <div className="mt-1">
          {imageStatus === "done" && post.image_type !== "carousel" && imageUrl && (
            <img
              src={imageUrl}
              alt={IMAGE_TYPE_LABELS[post.image_type] || "Post visual"}
              className="w-full border-y border-border object-cover"
            />
          )}
          {imageStatus === "done" && post.image_type === "carousel" && carouselUrls.length > 0 && (
            <div className="relative">
              <img
                src={carouselUrls[carouselIdx]}
                alt={`Slide ${carouselIdx + 1} of ${carouselUrls.length}`}
                className="w-full border-y border-border object-cover"
              />
              {carouselUrls.length > 1 && (
                <>
                  <button
                    onClick={() => setCarouselIdx(i => Math.max(0, i - 1))}
                    disabled={carouselIdx === 0}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 disabled:opacity-30 transition-opacity"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setCarouselIdx(i => Math.min(carouselUrls.length - 1, i + 1))}
                    disabled={carouselIdx === carouselUrls.length - 1}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 disabled:opacity-30 transition-opacity"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <div className="flex justify-center gap-1.5 mt-2 pb-2">
                    {carouselUrls.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCarouselIdx(i)}
                        className={`w-1.5 h-1.5 rounded-full transition-colors ${i === carouselIdx ? "bg-primary" : "bg-muted-foreground/30"}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          {imageStatus !== "done" && (
            <div className="px-4 pb-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowGenerateConfirm(true)}
                disabled={isGeneratingImage || imageStatus === "generating"}
                className="w-full border-dashed text-muted-foreground"
              >
                {isGeneratingImage || imageStatus === "generating" ? (
                  <><RefreshCw className="w-3.5 h-3.5 mr-2 animate-spin" />Generating {IMAGE_TYPE_LABELS[post.image_type]}…</>
                ) : (
                  <><Image className="w-3.5 h-3.5 mr-2" />Generate {IMAGE_TYPE_LABELS[post.image_type]}</>
                )}
              </Button>
              <AlertDialog open={showGenerateConfirm} onOpenChange={setShowGenerateConfirm}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Generate {IMAGE_TYPE_LABELS[post.image_type]}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will generate a {IMAGE_TYPE_LABELS[post.image_type]?.toLowerCase()} using AI image generation credits.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleGenerateImage}>Generate</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </div>
      )}

      {/* ── Action bar: 1 primary CTA + icon buttons ── */}
      <div className="border-t border-border px-3 py-2.5 flex items-center gap-2">
        {/* Primary: Copy to LinkedIn */}
        <button
          onClick={() => onCopy(post)}
          data-testid={`copy-btn-${post.id}`}
          className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${
            copiedId === post.id
              ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
              : "bg-indigo-600 text-white hover:bg-indigo-700"
          }`}
        >
          {copiedId === post.id ? (
            <><Check className="w-4 h-4" /> Copied!</>
          ) : (
            <><Copy className="w-4 h-4" /> Copy to LinkedIn</>
          )}
        </button>

        {/* Secondary: Mark used */}
        <button
          onClick={() => onMarkUsed(post)}
          data-testid={`mark-used-btn-${post.id}`}
          title="Mark as used"
          className="w-9 h-9 flex items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 transition-colors shrink-0"
        >
          <Check className="w-4 h-4" />
        </button>

        {/* Secondary: Edit */}
        <button
          onClick={handleEditStart}
          data-testid={`edit-btn-${post.id}`}
          title="Edit post"
          className="w-9 h-9 flex items-center justify-center rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 hover:bg-blue-500/20 transition-colors shrink-0"
        >
          <Pencil className="w-4 h-4" />
        </button>

        {/* Download — only when image ready */}
        {imageStatus === "done" && post.image_type && (
          <button
            onClick={handleDownload}
            disabled={isDownloading}
            data-testid={`download-btn-${post.id}`}
            title={post.image_type === "carousel" ? "Download as PDF" : "Download PNG"}
            className="w-9 h-9 flex items-center justify-center rounded-lg bg-slate-500/10 text-slate-600 dark:text-slate-400 hover:bg-slate-500/20 transition-colors shrink-0 disabled:opacity-50"
          >
            {isDownloading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          </button>
        )}

        {/* Skip — ghost border, lowest visual weight */}
        <button
          onClick={() => onSkip(post)}
          data-testid={`skip-btn-${post.id}`}
          title="Skip this post"
          className="w-9 h-9 flex items-center justify-center rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0"
        >
          <SkipForward className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const PostSkeleton = () => (
  <div className="bg-card rounded-xl border border-border overflow-hidden">
    <div className="flex items-start gap-3 px-4 pt-4 pb-2">
      <Skeleton className="w-9 h-9 rounded-full shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-3.5 w-28" />
        <Skeleton className="h-3 w-20" />
      </div>
    </div>
    <div className="px-4 pb-3 space-y-2">
      <Skeleton className="h-3.5 w-full" />
      <Skeleton className="h-3.5 w-full" />
      <Skeleton className="h-3.5 w-4/5" />
      <Skeleton className="h-3.5 w-full" />
      <Skeleton className="h-3.5 w-3/5" />
    </div>
    <div className="border-t border-border px-3 py-2.5 flex gap-2">
      <Skeleton className="h-9 flex-1 rounded-lg" />
      <Skeleton className="h-9 w-9 rounded-lg" />
      <Skeleton className="h-9 w-9 rounded-lg" />
      <Skeleton className="h-9 w-9 rounded-lg" />
    </div>
  </div>
);

const FeedPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isPro, isTrialActive } = useSubscription();
  const [posts, setPosts] = useState([]);
  const [userTopics, setUserTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [starredIds, setStarredIds] = useState(new Set());
  const [upgradePromptCode, setUpgradePromptCode] = useState(null);

  const fetchPosts = useCallback(async (pageNum = 1, topic = null, append = false, section = null) => {
    try {
      if (pageNum === 1) setIsLoading(true);
      else setIsLoadingMore(true);

      const params = new URLSearchParams({ page: pageNum, limit: 10 });
      if (section) params.append("section", section);
      else if (topic) params.append("topic", topic);

      const response = await api.get(`/posts?${params}`);
      const newPosts = response.data.posts;

      if (append) {
        setPosts((prev) => [...prev, ...newPosts]);
      } else {
        setPosts(newPosts);
      }

      setHasMore(response.data.has_more);
      setPage(pageNum);
      setIsGenerating(response.data.generating || false);
    } catch (error) {
      console.error("Failed to fetch posts:", error);
      toast.error("Failed to load posts");
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, []);

  const fetchUserTopics = useCallback(async () => {
    try {
      const response = await api.get("/user/topics");
      setUserTopics(response.data.topics);
    } catch (error) {
      console.error("Failed to fetch user topics:", error);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
    fetchUserTopics();
  }, [fetchPosts, fetchUserTopics]);

  // Poll every 10 seconds while posts are being generated
  useEffect(() => {
    if (!isGenerating || posts.length > 0) return;
    const interval = setInterval(() => { fetchPosts(); }, 10000);
    return () => clearInterval(interval);
  }, [isGenerating, posts.length, fetchPosts]);

  const handleCopy = async (post) => {
    try {
      await navigator.clipboard.writeText(cleanPostContent(post.variant_content));
      setCopiedId(post.id);
      await api.post(`/posts/${post.id}/use`);
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
      toast.success("Copied! Ready to paste on LinkedIn.");
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      const code = error?.response?.data?.detail?.code;
      if (code === "trial_expired" || code === "daily_limit_reached") {
        setUpgradePromptCode(code);
      } else {
        toast.error("Failed to copy post");
      }
    }
  };

  const handleMarkUsed = async (post) => {
    try {
      await api.post(`/posts/${post.id}/use`);
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
      toast.success("Marked as used");
    } catch (error) {
      const code = error?.response?.data?.detail?.code;
      if (code === "trial_expired" || code === "daily_limit_reached") {
        setUpgradePromptCode(code);
      } else {
        toast.error("Failed to mark post as used");
      }
    }
  };

  const handleSkip = async (post) => {
    try {
      await api.post(`/posts/${post.id}/skip`);
      setPosts((prev) => {
        const filtered = prev.filter((p) => p.id !== post.id);
        return [...filtered, post];
      });
    } catch (error) {
      console.error("Failed to skip:", error);
    }
  };

  const handleEditPost = (postId, newContent) => {
    setPosts((prev) =>
      prev.map((p) => p.id === postId ? { ...p, variant_content: newContent } : p)
    );
  };

  const handleStar = async (post) => {
    const isStarred = starredIds.has(post.id);
    try {
      if (isStarred) {
        await api.post(`/posts/${post.id}/unstar`);
        setStarredIds((prev) => { const next = new Set(prev); next.delete(post.id); return next; });
        toast.info("Removed from saved");
      } else {
        await api.post(`/posts/${post.id}/star`);
        setStarredIds((prev) => new Set(prev).add(post.id));
        toast.success("Saved!");
      }
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const handleTopicFilter = (topic) => {
    setSelectedTopic(topic);
    if (topic === "__trending") {
      fetchPosts(1, null, false, "trending");
    } else if (topic === "__others") {
      fetchPosts(1, null, false, "others");
    } else {
      fetchPosts(1, topic);
    }
  };

  const handleLoadMore = () => {
    if (selectedTopic === "__trending") fetchPosts(page + 1, null, true, "trending");
    else if (selectedTopic === "__others") fetchPosts(page + 1, null, true, "others");
    else fetchPosts(page + 1, selectedTopic, true);
  };

  const handleRefresh = () => {
    if (selectedTopic === "__trending") fetchPosts(1, null, false, "trending");
    else if (selectedTopic === "__others") fetchPosts(1, null, false, "others");
    else fetchPosts(1, selectedTopic);
    toast.success("Feed refreshed");
  };

  const headerRight = (
    <Button variant="ghost" size="icon" onClick={handleRefresh} data-testid="refresh-btn" title="Refresh feed" className="w-8 h-8">
      <RefreshCw className="w-4 h-4" />
    </Button>
  );

  return (
    <AppShell
      activeNav="feed"
      userTopics={userTopics}
      selectedTopic={selectedTopic}
      onTopicFilter={handleTopicFilter}
      headerRight={headerRight}
    >
      {/* Upgrade prompt modal */}
      {upgradePromptCode && (
        <UpgradePrompt
          code={upgradePromptCode}
          onClose={() => setUpgradePromptCode(null)}
        />
      )}

      {/* Trial expired full-page gate (not a banner) */}
      {!isPro && !isTrialActive && (
        <div className="px-4 py-2 text-center text-sm flex items-center justify-center gap-3 bg-destructive/10 text-destructive">
          <span>Your free trial has ended.</span>
          <button
            onClick={() => navigate("/pricing")}
            className="font-semibold underline underline-offset-2 hover:opacity-80 transition-opacity"
          >
            Subscribe to continue
          </button>
        </div>
      )}

      <div className="py-6 px-4 sm:px-6 max-w-5xl mx-auto">
        {/* Mobile topic chips */}
        {userTopics.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-5 lg:hidden">
            {[null, ...userTopics, "__trending", "__others"].map((value) => {
              const label = value === null ? "All" : value === "__trending" ? "🔥 Trending" : value === "__others" ? "Others" : (TOPIC_LABELS[value] || value);
              const active = selectedTopic === value;
              return (
                <button
                  key={value ?? "all"}
                  onClick={() => handleTopicFilter(value)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                    active
                      ? "bg-primary text-primary-foreground"
                      : "bg-card text-muted-foreground border border-border hover:border-primary/40 hover:text-foreground"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}

        {/* Posts */}
        {isLoading ? (
          <>
            <div className="hidden xl:flex gap-4 items-start">
              <div className="flex-1 flex flex-col gap-4"><PostSkeleton /><PostSkeleton /></div>
              <div className="flex-1 flex flex-col gap-4"><PostSkeleton /></div>
            </div>
            <div className="flex xl:hidden flex-col gap-4">
              {[...Array(3)].map((_, i) => <PostSkeleton key={i} />)}
            </div>
          </>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            {isGenerating ? (
              <>
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <RefreshCw className="w-6 h-6 text-primary animate-spin" />
                </div>
                <h3 className="text-base font-semibold text-foreground mb-2">Generating your posts…</h3>
                <p className="text-sm text-muted-foreground mb-4 max-w-sm mx-auto">
                  We're crafting personalized posts from your topics. This takes a few minutes.
                </p>
                <Button onClick={handleRefresh} variant="outline" size="sm">
                  <RefreshCw className="w-3.5 h-3.5 mr-2" />Check for new posts
                </Button>
              </>
            ) : (
              <>
                <div className="w-14 h-14 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-6 h-6 text-muted-foreground" />
                </div>
                <h3 className="text-base font-semibold text-foreground mb-2">All caught up!</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  You've gone through all posts for your selected topics.
                </p>
                <Button onClick={() => navigate("/settings")} variant="outline" size="sm">
                  <Settings className="w-3.5 h-3.5 mr-2" />Manage Topics
                </Button>
              </>
            )}
          </div>
        ) : (
          <>
            {/* Two independent masonry columns on wide screens */}
            <div className="hidden xl:flex gap-4 items-start">
              {[posts.filter((_, i) => i % 2 === 0), posts.filter((_, i) => i % 2 === 1)].map(
                (col, ci) => (
                  <div key={ci} className="flex-1 min-w-0 flex flex-col gap-4">
                    {col.map((post) => (
                      <PostCard
                        key={post.id}
                        post={post}
                        user={user}
                        onCopy={handleCopy}
                        onMarkUsed={handleMarkUsed}
                        onSkip={handleSkip}
                        onEdit={handleEditPost}
                        onStar={handleStar}
                        onUpgradeRequired={setUpgradePromptCode}
                        copiedId={copiedId}
                        starredIds={starredIds}
                      />
                    ))}
                  </div>
                )
              )}
            </div>
            {/* Single column on smaller screens */}
            <div className="flex xl:hidden flex-col gap-4">
              {posts.map((post) => (
                <PostCard
                  key={post.id}
                  post={post}
                  user={user}
                  onCopy={handleCopy}
                  onMarkUsed={handleMarkUsed}
                  onSkip={handleSkip}
                  onEdit={handleEditPost}
                  onStar={handleStar}
                  onUpgradeRequired={setUpgradePromptCode}
                  copiedId={copiedId}
                  starredIds={starredIds}
                />
              ))}
            </div>

            {hasMore && (
              <div className="flex justify-center mt-8">
                <Button
                  variant="outline"
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                  data-testid="load-more-btn"
                  className="px-8"
                >
                  {isLoadingMore ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Loading…</>
                  ) : (
                    "Load More"
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
};

export default FeedPage;
